import chartTheme from "@components/Charts/chartTheme";
import TitledChartSkeleton from "@components/TitledChartSkeleton";
import { ClientContext } from "@context/Client/ClientContext";
import { BarChart, BarChartTooltip } from "@dive.tech/ui-graphs";
import SwipeIcon from "@mui/icons-material/Swipe";
import { Box, Paper, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useModelKpiBreakdown } from "@services/api";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

const ModelKpiBreakdown = () => {
  const { currentClient } = useContext(ClientContext);
  const theme = useTheme();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const mdValue = theme.breakpoints.values.md; // 900

  const { data, isFetching: isLoadingData } = useModelKpiBreakdown(
    currentClient.model.id
  );

  // Ref and state for dynamic width
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const [chartWidth, setChartWidth] = useState<number>(mdValue);

  const chartData = useMemo(
    () =>
      (data || []).map((weekData) => ({
        date: weekData.week_id,
        [t("mmmodel.inflow_breakdown.chart.legend.baseline")]:
          weekData.baseline,
        [t("mmmodel.inflow_breakdown.chart.legend.incremental_effect")]:
          weekData.incremental_effect,
        [t("mmmodel.inflow_breakdown.chart.legend.external_effect")]:
          weekData.external_effect
      })),
    [data, t]
  );

  // Measure container width on mount and resize
  useEffect(() => {
    const updateWidth = () => {
      if (chartContainerRef.current) {
        const width = chartContainerRef.current.offsetWidth;
        setChartWidth(width > 0 ? width : 1000);
      }
    };

    updateWidth();

    // Use ResizeObserver for better performance
    const resizeObserver = new ResizeObserver(updateWidth);
    if (chartContainerRef.current) {
      resizeObserver.observe(chartContainerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <Box className="w-full pt-1 pl-4 pr-4" sx={{ height: "100%" }}>
      <Paper elevation={1} sx={{ height: "100%" }} ref={chartContainerRef}>
        {isLoadingData ? (
          <TitledChartSkeleton />
        ) : (
          <React.Fragment>
            <Typography variant="h6" component="div" className="pt-4 pl-6 pb-6">
              {t("mmmodel.inflow_breakdown.title", {
                kpi: currentClient.client.kpi_label
              })}
            </Typography>
            <Box
              sx={{
                overflowX: isMobile ? "scroll" : "none", // Estilos para "quitar" la barra de scroll
                scrollbarWidth: "none", // Estilos para "quitar" la barra de scroll - Firefox
                "&::-webkit-scrollbar": {
                  display: "none" // Estilos para "quitar" la barra de scroll - Chrome, Safari y Edge
                },
                height: "100%"
              }}
            >
              <Box
                sx={{
                  height: "100%",
                  width: isMobile ? mdValue : chartWidth,
                  display: "flex"
                }}
              >
                <BarChart
                  data={chartData || []}
                  responsiveBarProps={{
                    theme: chartTheme,
                    tooltip: (props: any) =>
                      BarChartTooltip({
                        color: props.color,
                        label: props.label,
                        value: props.value,
                        id: props.id,
                        index: props.index,
                        indexValue: props.indexValue,
                        data: props.data,
                        yAxis: { yType: "number", yDecimals: 0 },
                        formattedValue: props.formattedValue,
                        hidden: props.hidden
                      }),
                    layers: undefined,
                    padding: 1 / 10,
                    groupMode: "stacked",
                    enableLabel: false,
                    indexScale: { type: "band", round: true },
                    valueScale: { type: "linear", min: 0, max: 100 },
                    indexBy: "date",
                    keys: [
                      t("mmmodel.inflow_breakdown.chart.legend.baseline"),
                      t(
                        "mmmodel.inflow_breakdown.chart.legend.incremental_effect"
                      ),
                      t("mmmodel.inflow_breakdown.chart.legend.external_effect")
                    ],
                    colors: ["#667085", "#6172F3", "#85E139"],
                    axisLeft: {
                      tickSize: 10,
                      legend: `${currentClient.client.kpi_label} (%)`,
                      legendOffset: -70,
                      legendPosition: "middle"
                    },
                    axisBottom: {
                      tickRotation: -45,
                      tickPadding: 20,
                      legendPosition: "middle"
                    },
                    margin: { top: 34, left: 90, right: 10, bottom: 160 },
                    enableGridX: false,
                    enableGridY: false,
                    width: isMobile ? mdValue : chartWidth,
                    height: isMobile ? 450 : 500,
                    legends: [
                      {
                        dataFrom: "keys",
                        anchor: "top-right",
                        direction: "row",
                        toggleSerie: true,
                        translateX: -50,
                        translateY: -34,
                        itemsSpacing: 10,
                        itemWidth: 100,
                        itemHeight: 10,
                        itemDirection: "left-to-right",
                        itemOpacity: 1,
                        symbolSize: 15,
                        padding: {
                          bottom: 120
                        }
                      }
                    ]
                  }}
                />
                {isMobile ? (
                  <SwipeIcon
                    sx={{
                      bottom: 12,
                      opacity: 0.4,
                      left: "50%",
                      transform: "translate(750%, 0%)",
                      color: theme.palette.primary.main
                    }}
                    fontSize="small"
                  />
                ) : null}
              </Box>
            </Box>
          </React.Fragment>
        )}
      </Paper>
    </Box>
  );
};

export default ModelKpiBreakdown;
